import { useCallback, useEffect, useRef, useState } from 'react';
import { useLatestRef } from '@gonfalon/react/useLatestRef';
import { useSdkActive } from '@gonfalon/rest-api/hooks/useSdkActive';
import { POLL_ERROR_LABEL_META_KEY } from '@gonfalon/rest-api/pollErrorLabel';
import type { SdkKey } from '@gonfalon/sdk-info/sdkInfo';

import { makeRefetchInterval } from './makeRefetchInterval';

/** Surface the troubleshooting helper after 2 minutes of polling with no event. */
export const TROUBLESHOOTING_THRESHOLD_MS = 120000;
const ELAPSED_TICK_MS = 1000;

type UseSdkActivePollingOptions = {
  enabled?: boolean;
  /**
   * Maximum total fetch attempts (initial fetch + retries) before polling stops.
   * The initial fetch counts as attempt 1, so maxAttempts=3 means 1 initial + 2 retries.
   */
  maxAttempts?: number;
  maxDurationMs?: number;
  onStop?: () => void;
  /**
   * Keep polling after a failed request instead of stopping. The SDK-setup
   * connection indicator opts into this: a transient error during a long SDK
   * install shouldn't permanently halt the check, and a later successful poll
   * clears the error on its own. No effect in maxAttempts/maxDurationMs modes.
   */
  keepPollingOnError?: boolean;
  /**
   * Track elapsed time since polling started and expose it as `elapsedMs`, ticking
   * once a second. Opt-in so callers that don't render time-based copy pay no
   * per-second re-render cost. Resets whenever the SDK selection changes.
   */
  reportElapsed?: boolean;
};

export function useSdkActivePolling(
  sdkKey: SdkKey | null | undefined,
  {
    enabled = true,
    maxAttempts,
    maxDurationMs,
    onStop,
    keepPollingOnError = false,
    reportElapsed = false,
  }: UseSdkActivePollingOptions = {},
) {
  const attemptCountRef = useRef(0);
  // 0 means uninitialized; the duration check is skipped until the timer starts.
  const startTimeRef = useRef(0);
  const onStopRef = useLatestRef(onStop);

  useEffect(() => {
    if (enabled && maxDurationMs !== undefined) {
      startTimeRef.current = Date.now();
    }
  }, [enabled, maxDurationMs]);

  // Opt-in elapsed-time tracking for time-based copy (e.g. a troubleshooting hint
  // after N minutes). Opt-in so callers that don't render time-based copy pay no
  // per-second re-render. To reset on SDK change, consumers remount via a `key`
  // prop rather than resetting state from an effect here.
  const elapsedStartRef = useRef(0);
  const [elapsedMs, setElapsedMs] = useState(0);
  useEffect(() => {
    if (!enabled || !reportElapsed) {
      // Clear any stale elapsed time so a later re-enable (e.g. switching setup
      // method away and back) starts a fresh window rather than reusing the old
      // value — otherwise time-based copy/diagnosis would fire immediately.
      setElapsedMs(0);
      return undefined;
    }
    // Reset synchronously on (re)start so no stale value is read before the first tick.
    setElapsedMs(0);
    elapsedStartRef.current = Date.now();
    const id = setInterval(() => {
      setElapsedMs(Date.now() - elapsedStartRef.current);
    }, ELAPSED_TICK_MS);
    return () => clearInterval(id);
  }, [enabled, reportElapsed]);

  const {
    data,
    isLoading,
    isFetching,
    error,
    refetch: queryRefetch,
  } = useSdkActive(
    { sdkKey: sdkKey ?? undefined },
    {
      enabled,
      retry: false,
      // Names this poll for the query cache, which reports its failures once per outage.
      meta: { [POLL_ERROR_LABEL_META_KEY]: 'Error polling for SDK activation' },
      refetchInterval: makeRefetchInterval({
        onStopRef,
        attemptCountRef,
        startTimeRef,
        maxAttempts,
        maxDurationMs,
        keepPollingOnError,
      }),
    },
  );

  useEffect(() => {
    if (maxAttempts !== undefined && isFetching) {
      attemptCountRef.current += 1;
    }
  }, [isFetching, maxAttempts]);

  const refetch = useCallback(() => {
    if (maxDurationMs !== undefined) {
      startTimeRef.current = Date.now();
    }
    return queryRefetch();
  }, [maxDurationMs, queryRefetch]);

  return {
    data,
    isLoading,
    error,
    refetch,
    isActive: !!data?.active,
    elapsedMs,
  };
}
